import { useEffect, useState, type FormEvent } from 'react'
import { BellRing, Check, Mail, MessageSquareText, Send, Trash2 } from 'lucide-react'
import { HelpIcon, Button, EndpointHint, FORM_INPUT_CLASS, PhoneInput, Switch } from '../../core/index.js'
import { describeDevice, deviceContactId, pushSupport, rememberDeviceContact, subscribeToPush, unsubscribeFromPush } from '../delivery/webPush.js'
import { useAgreeContact, useForgetContact, useRegisterContact, useUpdateContact, useUpdateSmsGroups, useVerifyContact, useWorkerContacts } from '../hooks.js'
import type { ChannelDescriptor, ContactView, ContactsView, NotificationGroup } from '../types.js'
import { LoadFailure } from '../../core/index.js'

/** The words the worker agrees to, kept with the contact (plan D15). */
const CONSENT_WORDING: Record<string, string> = {
  web_push: 'Alert me on this device when a Bridge shift opens or my facility replies to me.',
  email: 'Email me when a Bridge shift opens or my facility replies to me. Nothing about my score goes by email.',
  sms: 'Text me when a Bridge shift opens or my facility replies to me. Message and data rates may apply; reply STOP to opt out.',
}

const ICONS = { web_push: BellRing, email: Mail, sms: MessageSquareText } as const

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/**
 * The Notifications card on the worker's Home (Clinical Bridge plan
 * section 13.6): one row per channel this deployment offers. Push is a
 * button that sets this device up; email and SMS take an address, the
 * consent, then the code that comes back. Each contact has the worker's
 * switch and Forget.
 */
export function WorkerContactsCard({ workerId }: { workerId: string }) {
  const contacts = useWorkerContacts(workerId)

  if (contacts.isPending) {
    return null
  }
  if (contacts.isError) {
    return (
      <section>
        <CardHeading />
        <LoadFailure size="xs" className="bg-white border border-marine/15 p-3" message="Your alert settings could not be loaded." onRetry={() => void contacts.refetch()} retrying={contacts.isFetching} />
      </section>
    )
  }
  const { channels, available } = contacts.data
  const smsOffered = available.some((channel) => channel.key === 'sms')
  const smsVerified = channels.some((contact) => contact.channel === 'sms' && contact.verifiedAt !== null)
  return (
    <section>
      <CardHeading />
      <div className="bg-white border border-marine/15 divide-y divide-marine/10">
        {available.length === 0 && <p className="p-3 text-[11px] text-ink-mid">Alerts outside the app are not set up on this deployment yet. Your messages are here when you open autonoME.</p>}
        {available.map((channel) => (
          <ChannelRow key={channel.key} workerId={workerId} channel={channel} contacts={channels.filter((contact) => contact.channel === channel.key)} />
        ))}
        {smsVerified && contacts.data.smsGroups && contacts.data.groups && <SmsGroupSwitches workerId={workerId} view={contacts.data} />}
        {!smsOffered && (
          <div className="flex items-center gap-2.5 p-3 text-[11px] text-ink-mid/70">
            <MessageSquareText aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span>
              <span className="font-bold">Text messages</span> · coming later
            </span>
          </div>
        )}
      </div>
      <p className="mt-1.5 text-[10px] text-ink-mid/80 leading-relaxed">An alert says only that something is waiting and opens the app. Your score, your tier and your pay never leave autonoME.</p>
    </section>
  )
}

/** The four groups a worker chooses texts by (SMS plan section 3.2), shown once a number is verified. The facility's own settings bound them. */
function SmsGroupSwitches({ workerId, view }: { workerId: string; view: ContactsView }) {
  const update = useUpdateSmsGroups(workerId)
  const groups = view.smsGroups as Record<NotificationGroup, boolean>
  return (
    <div className="p-3">
      <div className="text-[11px] font-bold text-ink">What reaches you by text</div>
      <p className="mt-0.5 text-[10.5px] text-ink-mid/80">Everything is still in the app. Your facility chooses which kinds of message it texts at all; these are yours within that.</p>
      <div className="mt-2 flex flex-col gap-2">
        {(view.groups ?? []).map((group) => (
          <div key={group.key} className="flex items-center justify-between gap-3 text-[11px]">
            <span className="min-w-0">
              <span className="text-ink">{group.label}</span>
              <span className="block text-[10px] text-ink-mid/80">{group.description}</span>
            </span>
            <Switch checked={groups[group.key]} onChange={(enabled) => update.mutate({ [group.key]: enabled })} label={`Text me ${group.label.toLowerCase()}`} disabled={update.isPending} />
          </div>
        ))}
      </div>
      {update.isError && <p className="mt-1 text-[11px] text-coral">{errorText(update.error, 'That could not be saved.')}</p>}
    </div>
  )
}

function CardHeading() {
  return (
    <div className="mb-1.5 flex items-baseline gap-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
      Notifications
      <HelpIcon topic="messages.worker-contacts" />
      <EndpointHint>GET /workers/{'{workerId}'}/contacts</EndpointHint>
    </div>
  )
}

interface ChannelRowProps {
  workerId: string
  channel: ChannelDescriptor
  contacts: readonly ContactView[]
}

function ChannelRow({ workerId, channel, contacts }: ChannelRowProps) {
  const Icon = ICONS[channel.key as keyof typeof ICONS] ?? BellRing
  return (
    <div className="p-3">
      <div className="flex items-center gap-2.5">
        <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-marine" />
        <div className="text-[11px] font-bold text-ink">{channel.label}</div>
      </div>
      <div className="mt-2 flex flex-col gap-2 pl-6">
        {contacts.map((contact) => (
          <ContactRow key={contact.contactId} workerId={workerId} contact={contact} />
        ))}
        {channel.key === 'web_push' ? (
          <PushSetup workerId={workerId} channel={channel} contacts={contacts} />
        ) : (
          <AddressSetup workerId={workerId} channel={channel} existing={contacts[0] ?? null} />
        )}
      </div>
    </div>
  )
}

function ContactRow({ workerId, contact }: { workerId: string; contact: ContactView }) {
  const update = useUpdateContact(workerId)
  const forget = useForgetContact(workerId)
  if (contact.awaitingAgreement) {
    return <AgreeRow workerId={workerId} contact={contact} />
  }
  const isThisDevice = contact.channel === 'web_push' && deviceContactId() === contact.contactId
  async function handleForget(): Promise<void> {
    await forget.mutateAsync(contact.contactId)
    if (isThisDevice) {
      rememberDeviceContact(null)
      await unsubscribeFromPush()
    }
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
      <span className="min-w-0">
        <span className="text-ink">{contact.label ?? contact.address}</span>
        {contact.label && <span className="text-ink-mid"> · {contact.address}</span>}
        {isThisDevice && <span className="text-ink-mid"> · this device</span>}
        {contact.verifiedAt === null && <span className="text-gold"> · waiting for your code</span>}
        {contact.optedOut && <span className="text-gold"> · you replied STOP; text START to our number or switch texts back on here</span>}
        {!contact.optedOut && contact.failures > 0 && <span className="text-coral"> · not getting through</span>}
      </span>
      <span className="flex items-center gap-3">
        <Switch checked={contact.enabled} onChange={(enabled) => update.mutate({ contactId: contact.contactId, enabled })} label={`${contact.channelLabel} ${contact.address}`} disabled={update.isPending} />
        <button type="button" onClick={() => void handleForget()} disabled={forget.isPending} className="flex items-center gap-1 text-[11px] font-bold text-ink-mid hover:text-coral" aria-label={`Forget ${contact.address}`}>
          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          Forget
        </button>
      </span>
      {(update.isError || forget.isError) && <p className="w-full text-coral">{errorText(update.error ?? forget.error, 'That could not be saved.')}</p>}
    </div>
  )
}

/** A number the worker's facility entered (SMS plan 3.5): the consent is the worker's, given here or by replying YES; Not me forgets it. */
function AgreeRow({ workerId, contact }: { workerId: string; contact: ContactView }) {
  const agree = useAgreeContact(workerId)
  const forget = useForgetContact(workerId)
  const [agreed, setAgreed] = useState(false)
  const wording = CONSENT_WORDING['sms'] ?? ''
  return (
    <div className="flex flex-col gap-1.5 text-[11px]">
      <p className="text-ink">
        Your facility added <span className="font-bold">{contact.address}</span>. Nothing is texted there until you agree: reply YES to the text we sent, or agree here.
      </p>
      <>
          <label className="flex items-start gap-2 text-ink-mid">
            <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} className="mt-0.5" />
            <span>{wording}</span>
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <Button icon={Check} size="sm" disabled={!agreed || agree.isPending} onClick={() => agree.mutate({ contactId: contact.contactId, wording })}>
              {agree.isPending ? 'Sending a code…' : 'Agree and verify'}
            </Button>
            <button type="button" onClick={() => forget.mutate(contact.contactId)} disabled={forget.isPending} className="flex items-center gap-1 font-bold text-ink-mid hover:text-coral">
              <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
              Not me
            </button>
          </div>
          {(agree.isError || forget.isError) && <p className="text-coral">{errorText(agree.error ?? forget.error, 'That could not be saved.')}</p>}
      </>
    </div>
  )
}

function PushSetup({ workerId, channel, contacts }: { workerId: string; channel: ChannelDescriptor; contacts: readonly ContactView[] }) {
  const register = useRegisterContact(workerId)
  const [support, setSupport] = useState<ReturnType<typeof pushSupport>>('unsupported')
  const [reason, setReason] = useState<string | null>(null)
  useEffect(() => {
    setSupport(pushSupport())
  }, [])
  const thisDevice = deviceContactId()
  const alreadyOn = thisDevice !== null && contacts.some((contact) => contact.contactId === thisDevice)

  async function turnOn(): Promise<void> {
    setReason(null)
    if (!channel.publicKey) {
      setReason('This deployment has no push key yet.')
      return
    }
    const result = await subscribeToPush(channel.publicKey)
    if (!result.ok) {
      setReason(result.reason)
      return
    }
    try {
      const contact = await register.mutateAsync({ channel: 'web_push', address: result.subscription, consent: { wording: CONSENT_WORDING['web_push'] ?? '' }, label: describeDevice() })
      rememberDeviceContact(contact.contactId)
    } catch (error: unknown) {
      setReason(errorText(error, 'This device could not be registered.'))
    }
  }

  if (alreadyOn) {
    return <p className="flex items-center gap-1 text-[11px] text-green"><Check aria-hidden="true" className="h-3.5 w-3.5" /> Alerts are on for this device.</p>
  }
  if (support === 'install_first') {
    return <p className="text-[11px] text-ink-mid">On an iPhone, add autonoME to your home screen first: tap Share, then “Add to Home Screen”, and open it from there to turn on alerts.</p>
  }
  if (support === 'unsupported') {
    return <p className="text-[11px] text-ink-mid">This browser cannot show alerts from autonoME. Try your phone, or add an email below.</p>
  }
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] text-ink-mid">{CONSENT_WORDING['web_push']}</p>
      <div>
        <Button icon={BellRing} size="sm" onClick={() => void turnOn()} disabled={register.isPending}>
          {register.isPending ? 'Setting up…' : 'Turn on alerts on this device'}
        </Button>
      </div>
      {reason && <p className="text-[11px] text-coral">{reason}</p>}
    </div>
  )
}

function AddressSetup({ workerId, channel, existing }: { workerId: string; channel: ChannelDescriptor; existing: ContactView | null }) {
  const register = useRegisterContact(workerId)
  const verify = useVerifyContact(workerId)
  const [address, setAddress] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [code, setCode] = useState('')
  const wording = CONSENT_WORDING[channel.key] ?? `Reach me by ${channel.label.toLowerCase()} when a Bridge shift opens or my facility replies.`
  const pending = existing !== null && existing.verifiedAt === null
  const inputId = `contact-${channel.key}`

  async function handleRegister(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!agreed || address.trim() === '') {
      return
    }
    await register.mutateAsync({ channel: channel.key, address: address.trim(), consent: { wording } })
    setAddress('')
    setAgreed(false)
  }

  async function handleVerify(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!existing || !/^\d{6}$/.test(code)) {
      return
    }
    await verify.mutateAsync({ contactId: existing.contactId, code })
    setCode('')
  }

  if (pending) {
    return (
      <form onSubmit={(event) => void handleVerify(event)} className="flex flex-col gap-1.5">
        <label htmlFor={`${inputId}-code`} className="text-[11px] text-ink-mid">
          We sent a six-digit code to {existing.address}. Enter it here.
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input id={`${inputId}-code`} inputMode="numeric" pattern="\d{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} className={`${FORM_INPUT_CLASS} w-28`} placeholder="123456" />
          <Button type="submit" icon={Check} size="sm" disabled={verify.isPending || code.length !== 6}>
            {verify.isPending ? 'Checking…' : 'Verify'}
          </Button>
        </div>
        {verify.isError && <p className="text-[11px] text-coral">{errorText(verify.error, 'That is not the code.')}</p>}
        {existing.lockedUntil && <p className="text-[11px] text-ink-mid">Too many wrong codes; try again after {new Date(existing.lockedUntil).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.</p>}
      </form>
    )
  }
  if (existing) {
    return null
  }
  return (
    <form onSubmit={(event) => void handleRegister(event)} className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {channel.key === 'email' ? (
          <input
            id={inputId}
            type="email"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            className={`${FORM_INPUT_CLASS} min-w-0 flex-1`}
            placeholder="you@example.com"
            aria-label={channel.label}
            autoComplete="email"
          />
        ) : (
          <PhoneInput id={inputId} value={address} onChange={setAddress} className={FORM_INPUT_CLASS} aria-label={channel.label} autoComplete="tel" />
        )}
        <Button type="submit" icon={Send} size="sm" disabled={register.isPending || !agreed || address.trim() === ''}>
          {register.isPending ? 'Sending…' : 'Send code'}
        </Button>
      </div>
      <label className="flex items-start gap-2 text-[11px] text-ink-mid">
        <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} className="mt-0.5" />
        <span>{wording}</span>
      </label>
      {register.isError && <p className="text-[11px] text-coral">{errorText(register.error, 'That could not be saved.')}</p>}
    </form>
  )
}
